import React, { useState, useEffect, useCallback, useRef } from "react";
import ThinkingDots from "./ThinkingDots";
import { MarkdownRenderer } from "./markdownRenderer";
import StrategicToolbar from "./StrategicToolbar";
import { FiChevronDown, FiMenu, FiPaperclip, FiX } from "react-icons/fi";
import LimitReachedModal from "./LimitReachedModal";
import useUserPlan from "../hooks/useUserPlan";
import ChatExporter from "./ChatExporter";
import UpgradeToExportButton from "./UpgradeToExportButton";
import UpgradeModal from "./UpgradeModal";
import { LENSES } from "../utils/modes";
import Typewriter from "./Typewriter";
import useSendMessage from "../hooks/useSendMessage";

// --- keep-alive ping for Render ---
const ensureBackendAwake = async () => {
  try {
    const res = await fetch(`${"https://vintariai-backend-1.onrender.com"}/ping`);
    return res.ok;
  } catch {
    return false;
  }
};

export default function MainChatLayout({
  conversation,
  setConversation,
  setSidebarOpen,
  setCurrentChatId,
  currentChatId,
  fetchChatSessions,
  fetchChatHistory,
  loading,
  setLoading,
  messagesEndRef, // (unused but kept for API compatibility)
  input,
  setInput,
  user,
  supabase,
  sidebarOpen,
}) {
  useEffect(() => {
    document.body.classList.add('lock-window');
    return () => document.body.classList.remove('lock-window');
  }, []);


  // ── streaming + scroll management
  const scrollRef = useRef(null);
  const [stickToBottom, setStickToBottom] = useState(true);
  const [streamBuffer, setStreamBuffer] = useState("");
  const [lastPrompt, setLastPrompt] = useState("");

  const isNearBottom = (el, threshold = 80) =>
    el ? el.scrollHeight - el.scrollTop - el.clientHeight < threshold : true;

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setStickToBottom(isNearBottom(el));
  };

  // After AI finishes, keep view pinned to bottom if user was there.
  useEffect(() => {
    const el = scrollRef.current;
    if (loading || !el) return;
    if (stickToBottom) {
      // allow React layout to finish
      setTimeout(() => {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      }, 0);
    }
  }, [conversation?.messages, stickToBottom, loading]);

  const { send, stop, isStreaming } = useSendMessage({
    conversation,
    setConversation,
    currentChatId,
    fetchChatHistory,
    setCurrentChatId,
    fetchChatSessions,
    setLoading,
    setInput,
    onToken: (chunk) => setStreamBuffer((prev) => prev + chunk),
    onStreamDone: (full) => setStreamBuffer(full),
    onLimit: ({ limit, resetTime, plan }) => {
      setModalInfo({
        show: true,
        limit: limit ?? 5,
        resetTime: resetTime ?? "",
        plan: plan ?? "free",
      });
      setLoading(false);
    },
  });

  // ── state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeMode, setActiveMode] = useState(LENSES[0].label);
  const [easyMode, setEasyMode] = useState(false);
  const [creativeMode, setCreativeMode] = useState(false);
  const [wakingUp, setWakingUp] = useState(false);
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const userPlan = useUserPlan();

  // refs for layout/keyboard handling
  const inputRowRef = useRef(null);
  const textareaRef = useRef(null);
  const [inputStackHeight, setInputStackHeight] = useState(80);

  const [modalInfo, setModalInfo] = useState({
    show: false,
    limit: 0,
    plan: "free",
    resetTime: "00:00",
  });

  const normalizedPlan = userPlan ? userPlan.trim().toLowerCase() : "free";

  const [upgradeModal, setUpgradeModal] = useState({
    isOpen: false,
    requiredTier: "proOrElite",
  });

  const openUpgradeModal = (tier) => {
    setUpgradeModal({ isOpen: true, requiredTier: tier || "proOrElite" });
  };

  // ── wake backend + refresh on visibility
  const wakeAndLoad = useCallback(async () => {
    if (!currentChatId) return;
    let wakeTimeout = setTimeout(() => setWakingUp(true), 1000);
    const awake = await ensureBackendAwake();
    clearTimeout(wakeTimeout);

    if (awake) {
      setWakingUp(false);
      fetchChatHistory(currentChatId);
    } else {
      console.warn("⏳ Backend sleeping. Retrying in 3 seconds...");
      setWakingUp(true);
      setTimeout(async () => {
        setWakingUp(false);
        await fetchChatHistory(currentChatId);
      }, 3000);
    }
  }, [currentChatId, fetchChatHistory]);

  useEffect(() => {
    wakeAndLoad();
  }, [wakeAndLoad]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        wakeAndLoad();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [wakeAndLoad]);

  // ── measure the input row + safe area; follow keyboard (visualViewport)
  useEffect(() => {
    const row = inputRowRef.current;
    if (!row) return;

    const update = () => {
      const safe = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue("--safe-bottom") || "0",
        10
      );
      setInputStackHeight(row.offsetHeight + safe);
    };

    const ro = new ResizeObserver(update);
    ro.observe(row);

    const vv = window.visualViewport;
    vv && vv.addEventListener("resize", update);

    update(); // initial

    return () => {
      ro.disconnect();
      vv && vv.removeEventListener("resize", update);
    };
  }, []);

  // ── on focus, keep the chat scroller at bottom (no window panning)
  useEffect(() => {
    const ta = textareaRef.current;
    const scroller = scrollRef.current;
    if (!ta || !scroller) return;

    const onFocus = () => {
      window.scrollTo(0, 0);
      requestAnimationFrame(() => {
        scroller.scrollTo({ top: scroller.scrollHeight, behavior: "auto" });
      });
    };

    ta.addEventListener("focus", onFocus);
    return () => ta.removeEventListener("focus", onFocus);
  }, []);

  // ── simple toggles
  const toggleEasyMode = () => {
    setEasyMode((prev) => {
      const v = !prev;
      if (v) setCreativeMode(false);
      return v;
    });
  };

  const toggleCreativeMode = () => {
    setCreativeMode((prev) => {
      const v = !prev;
      if (v) setEasyMode(false);
      return v;
    });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) setFile(selectedFile);
  };

  // ── send
  const wrappedHandleSend = async (e, strategyPayload = null) => {
    if (e) e.preventDefault();

    const token = (await supabase.auth.getSession()).data.session?.access_token;
    if (!token) {
      console.error("Authentication token not found.");
      return;
    }

    const currentInput = strategyPayload ? strategyPayload.input : (input || "").trim();
    if (!currentInput && !file) return;

    setIsSubmitting(true);
    setLoading(true);
    setLastPrompt(currentInput);
    setStreamBuffer("");

    let resultMeta = null;

    try {
      resultMeta = await send({
        input: currentInput,
        file,
        systemPrompt: strategyPayload?.systemPrompt ?? null,
        visibleUserMessage: strategyPayload?.visibleUserMessage ?? currentInput,
        activeMode,
      });

      if (resultMeta?.aborted && resultMeta?.limitReached) {
        setIsSubmitting(false);
        setLoading(false);
        setInput(currentInput);
        return;
      }

      // smooth scroll after user's msg is added
      setTimeout(() => {
        const el = scrollRef.current;
        if (el) el.scrollTo({ top: el.scrollHeight, behavior: "auto" });
      }, 200);
    } catch (err) {
      console.error("Send failed:", err);
    } finally {
      setIsSubmitting(false);
      if (!(resultMeta?.aborted && resultMeta?.limitReached)) {
        setInput("");
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
      setLoading(false);
    }
  };

  return (
    <>
      <LimitReachedModal
        show={modalInfo.show}
        onClose={() => setModalInfo({ ...modalInfo, show: false })}
        limit={modalInfo.limit}
        plan={modalInfo.plan}
        resetTime={modalInfo.resetTime}
      />

      <UpgradeModal
        isOpen={upgradeModal.isOpen}
        requiredTier={upgradeModal.requiredTier}
        onClose={() => setUpgradeModal({ isOpen: false, requiredTier: "proOrElite" })}
        upgradeUrl="/upgrade"
      />

      {/* Main viewport area */}
      <div className="relative flex-1 w-full h-[100dvh] min-w-0 text-white">
        {/* Background */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#0c0718] via-black to-black" />
        <div className="pointer-events-none absolute inset-0 [background:radial-gradient(1200px_600px_at_50%_-10%,rgba(99,102,241,0.15),transparent)]" />

        {/* Grid layout: header | messages | toolbar | input */}
        <div className="relative grid h-full w-full grid-rows-[auto,1fr,auto,auto] overflow-hidden">
          {/* Top Bar */}
          <div className="sticky top-0 z-30 border-b border-white/10 bg-black/40 backdrop-blur px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {/* Mobile menu */}
                <div className="relative group md:hidden">
                  <div className="absolute -inset-0.5 bg-purple-600 rounded-lg blur opacity-75 group-hover:opacity-100 transition duration-200"></div>
                  <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="relative flex items-center bg-black text-white text-sm px-3 py-2 rounded-md"
                    aria-label="Toggle sidebar"
                  >
                    <FiMenu size={20} />
                  </button>
                </div>

                {/* Home */}
                <div className="relative group hidden md:flex">
                  <div className="absolute -inset-0.5 bg-purple-600 rounded-lg blur opacity-60 group-hover:opacity-100 transition duration-200"></div>
                  <button
                    onClick={() => (window.location.href = "/")}
                    className="relative items-center bg-black text-white text-sm px-4 py-2 rounded-md"
                  >
                    ← Home
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* Upgrade CTA (Free only) */}
                {normalizedPlan === "free" && (
                  <div className="relative group">
                    <div className="absolute -inset-0.5 bg-yellow-500 rounded-lg blur opacity-75 group-hover:opacity-100 transition duration-200"></div>
                    <button
                      onClick={() => (window.location.href = "/upgrade")}
                      className="relative bg-black text-yellow-300 font-semibold px-3 py-1.5 rounded shadow transition text-sm"
                    >
                      Upgrade Now
                    </button>
                  </div>
                )}

                {/* Export or upsell */}
                {normalizedPlan !== "free" ? (
                  <ChatExporter messages={conversation.messages} />
                ) : (
                  <UpgradeToExportButton />
                )}

                {/* Account dropdown */}
                <div className="relative">
                  <div className="relative group">
                    <div className="absolute -inset-0.5 bg-purple-600 rounded-lg blur opacity-75 group-hover:opacity-100 transition duration-200"></div>
                    <button
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                      className="relative flex items-center bg-black text-white px-3 py-2 rounded-md"
                      aria-label="Account menu"
                    >
                      <FiChevronDown />
                    </button>
                  </div>

                  {dropdownOpen && (
                    <div className="absolute right-0 mt-2 w-64 rounded-md backdrop-blur-md bg-black/80 border border-white/20 shadow-lg z-50 p-3 space-y-3 text-sm">
                      <div className="text-white border-b border-white/10 pb-2 space-y-1">
                        <div>{user?.email || "Account"}</div>
                        <div className="text-white/60 text-xs">
                          Plan:{" "}
                          <span
                            className={`uppercase font-semibold ${
                              normalizedPlan === "elite"
                                ? "text-yellow-400"
                                : normalizedPlan === "professional"
                                ? "text-purple-500 drop-shadow-sm"
                                : "text-white/50"
                            }`}
                          >
                            {userPlan ? userPlan.trim().toUpperCase() : "FREE"}
                          </span>
                        </div>
                      </div>

                      {normalizedPlan === "free" && (
                        <div className="pt-2 border-t border-white/10">
                          <a
                            href="/upgrade"
                            className="block text-center text-sm bg-indigo-500 hover:bg-indigo-600 text-white py-2 rounded transition"
                          >
                            Upgrade to Professional
                          </a>
                        </div>
                      )}

                      <button
                        onClick={async () => {
                          await supabase.auth.signOut();
                          window.location.href = "/login";
                        }}
                        className="w-full text-left text-white hover:bg-white/10 px-2 py-2 rounded"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Messages (Row 2 — the ONLY scroller) */}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="chat-messages min-w-0 overflow-y-auto overflow-x-hidden p-6 space-y-4"
            style={{
              overscrollBehaviorY: "contain",      // stop scroll chaining without JS
              WebkitOverflowScrolling: "touch",    // iOS momentum scroll
              paddingBottom: inputStackHeight,     // room above input stack
            }}
          >
            {wakingUp && (
              <div className="text-center text-sm text-white/70 italic mb-4 animate-pulse">
                🔄 Waking up server… please wait.
              </div>
            )}

            {normalizedPlan === "free" && (
              <div className="mx-auto mb-5 max-w-3xl rounded-2xl border border-purple-500/25 bg-gradient-to-br from-[#2a1b3d]/70 to-[#0f0a1a]/70 px-6 py-5 text-center shadow-[0_0_40px_rgba(120,60,255,0.12)]">
                <div className="text-xl font-bold tracking-tight">
                  Your strategist isn’t saving your breakthroughs.
                </div>
                <div className="mt-1 text-sm text-white/80">
                  Upgrade to keep every insight, search your history, and export plans on
                  demand.
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-center gap-2 text-xs text-white/80">
                  <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-1">
                    🔒 Save history
                  </span>
                  <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-1">
                    🔍 Global search
                  </span>
                  <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-1">
                    📤 One-click export
                  </span>
                </div>
                <a
                  href="/upgrade"
                  className="inline-flex items-center mt-4 rounded-xl bg-gradient-to-r from-yellow-300 to-amber-400 px-4 py-2 text-sm font-semibold text-black shadow transition hover:brightness-105"
                >
                  Upgrade for Full Access →
                </a>
              </div>
            )}

            {(conversation?.messages || [])
              .filter((msg) => msg.role !== "user" || msg.visible !== false)
              .map((msg, idx, messages) => (
                <div
                  key={idx}
                  className={`flex mb-2 ${
                    msg.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div className="relative max-w-2xl">
                    <div
                      className={`relative shadow-[0_2px_6px_rgba(0,0,0,0.35)]
                        px-4
                        ${msg.role === "user" ? "py-2 leading-6 rounded-2xl" : "py-3 leading-7 rounded-lg"}
                        text-[15px]
                        ${
                          msg.role === "user"
                            ? "bg-gray-800 text-white border border-gray-600"
                            : "bg-black text-white border border-purple-500/30"
                        }
                        [&>*:first-child]:mt-0 [&>*:last-child]:mb-0
                      `}
                    >
                      <MarkdownRenderer content={msg.content} />
                      {msg.role === "assistant" && idx === messages.length - 1 && loading && (
                        <Typewriter />
                      )}
                    </div>
                  </div>
                </div>
              ))}

            {loading && (
              <ThinkingDots isGenerating streamedText={streamBuffer} promptText={lastPrompt} />
            )}
          </div>

          {/* Toolbar (Row 3 — non-scrolling) */}
          <div className="border-t border-purple-500/10 bg-black/40 px-2 pb-[env(safe-area-inset-bottom)]">
            <StrategicToolbar
              onStrategyClick={(payload) => {
                wrappedHandleSend(null, payload);
              }}
              activeMode={activeMode}
              setUpgradeModalOpen={openUpgradeModal}
              setActiveMode={setActiveMode}
              easyMode={easyMode}
              creativeMode={creativeMode}
              setEasyMode={toggleEasyMode}
              setCreativeMode={toggleCreativeMode}
              setSidebarOpen={setSidebarOpen}
            />
          </div>

          {/* Input (Row 4 — non-scrolling) */}
          <div
            ref={inputRowRef}
            className="bg-black/60 p-2 border-t border-purple-500/10 pb-[env(safe-area-inset-bottom)]"
          >
            <form onSubmit={wrappedHandleSend} className="flex items-center">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="p-2 text-white/70 hover:text-white transition-colors"
                aria-label="Attach file"
              >
                <FiPaperclip size={20} />
              </button>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                accept="image/*,application/pdf,.txt,.md,.csv"
              />

              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="
                  flex-grow p-2 bg-gray-900 rounded text-white mx-2 max-h-24 outline-none
                  border border-white/10
                  focus:border-purple-400/60 focus:shadow-[0_0_0_3px_rgba(168,85,247,0.25)] transition
                  text-[17px] md:text-[15px]
                "
                placeholder="What’s your next move? Ask your strategist…"
                disabled={isSubmitting}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    wrappedHandleSend(e);
                  }
                  if (e.key === "Escape" && isStreaming) {
                    e.preventDefault();
                    stop();
                  }
                }}
              />

              <div>
                <button
                  type="submit"
                  disabled={isStreaming || (!input.trim() && !file)}
                  className="
                    px-4 py-2 rounded-lg text-sm
                    border border-purple-500/40 text-purple-200
                    hover:bg-purple-500/10 hover:border-purple-400 hover:text-purple-50
                    disabled:opacity-50 disabled:cursor-not-allowed
                    transition-colors
                  "
                >
                  {isStreaming ? "Streaming…" : "Send"}
                </button>
              </div>

              <div className="ml-2">
                {isStreaming ? (
                  <button
                    type="button"
                    onClick={stop}
                    className="
                      px-4 py-2 rounded-lg text-sm
                      bg-red-600/80 text-white
                      hover:bg-red-600
                      transition-colors animate-pulse
                    "
                    title="Stop response (Esc)"
                  >
                    Stop
                  </button>
                ) : null}
              </div>
            </form>

            {file && (
              <div className="px-3 py-1 text-xs text-white/80 flex items-center justify-between bg-gray-900 rounded mt-1">
                <span>
                  Attached: <strong>{file.name}</strong>
                </span>
                <button
                  onClick={() => {
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  className="p-1 hover:bg-white/20 rounded-full"
                >
                  <FiX size={14} />
                </button>
              </div>
            )}

            {normalizedPlan === "free" && (
              <div className="mt-2 flex items-center justify-between text-[11px] text-white/60">
                <span>Free Plan</span>
                <a
                  href="/upgrade"
                  className="underline decoration-dotted underline-offset-2 hover:text-white/80"
                >
                  Unlock unlimited saves & search →
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
